import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import ClinicInfoCard from '../components/common/ClinicInfoCard';
import DoctorsList from '../components/clinic/DoctorsList';
import ClinicImagesTab from '../components/clinic/ClinicImagesTab';
import ClinicServicesTab from '../components/clinic/ClinicServicesTab';
import BeforeAfterImagesTab from '../components/clinic/BeforeAfterImagesTab';
import { getClinicDetails } from '../services/api';
import { extractClinicIdFromSlug } from '../utils/clinicUtils';

const ClinicDetails = () => {
    const { id, slug } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const [clinicData, setClinicData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [activeTab, setActiveTab] = useState('details'); // 'details', 'services', 'images', 'clinicImages', 'doctors'

    // Determine clinic ID - either from direct id param or extracted from slug
    const clinicId = id || extractClinicIdFromSlug(slug);

    // Check if user came from management page
    const isFromManagement = location.state?.fromManagement || false;

    useEffect(() => {
        const fetchClinicDetails = async () => {
            try {
                setLoading(true);
                setError(null);
                
                const response = await getClinicDetails(clinicId);
                
                if (response.success) {
                    setClinicData(response.data);
                } else {
                    setError(response.message || 'Failed to fetch clinic details');
                }
            } catch (err) {
                console.error('Error fetching clinic details:', err);
                setError('Failed to fetch clinic details. Please try again.');
            } finally {
                setLoading(false);
            }
        };

        if (clinicId) {
            fetchClinicDetails();
        }
    }, [clinicId]);

    const handleBack = () => {
        navigate(-1);
    };

    // Management button handlers
    const handleManageServices = () => {
        setActiveTab('services');
    };

    const handleManageBeforeAfterImages = () => {
        setActiveTab('images');
    };

    const handleManageClinicImages = () => {
        setActiveTab('clinicImages');
    };

    const handleManageDoctors = () => {
        setActiveTab('doctors');
    };

    if (loading) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
                    <p className="text-gray-600">Loading clinic details...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="min-h-screen bg-gray-50 flex items-center justify-center">
                <div className="text-center max-w-md mx-auto p-6">
                    <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                        <h2 className="text-xl font-semibold text-red-800 mb-2">Error</h2>
                        <p className="text-red-600 mb-4">{error}</p>
                        <div className="flex space-x-3">
                            <button
                                onClick={handleBack}
                                className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                            >
                                <ArrowLeftIcon className="w-4 h-4 mr-2" />
                                Go Back
                            </button>
                            <button
                                onClick={() => window.location.reload()}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                            >
                                Try Again
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const renderContent = () => {
        switch (activeTab) {
            case 'services':
                return <ClinicServicesTab clinicId={clinicId} />;
            case 'images':
                return <BeforeAfterImagesTab clinicId={clinicId} />;
            case 'clinicImages':
                return <ClinicImagesTab clinicId={clinicId} />;
            case 'doctors':
                return <DoctorsList clinicId={clinicId} />;
            default:
                return <ClinicInfoCard clinicData={clinicData} />;
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <div className="bg-white shadow-sm border-b">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-between h-16">
                        <div className="flex items-center">
                            <button
                                onClick={handleBack}
                                className="flex items-center text-gray-600 hover:text-gray-900 transition-colors"
                            >
                                <ArrowLeftIcon className="w-5 h-5 mr-2" />
                                Back
                            </button>
                        </div>
                        <div className="text-center">
                            <h1 className="text-xl font-semibold text-gray-900">Clinic Details</h1>
                        </div>
                        <div className="w-20"></div> {/* Spacer for centering */}
                    </div>
                </div>
            </div>

            {/* Management Buttons - Only show if coming from management page */}
            {isFromManagement && (
                <div className="bg-white border-b border-gray-200">
                    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                        <div className="flex flex-wrap gap-2 sm:gap-3 justify-center">
                            <button
                                onClick={handleManageServices}
                                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                                    activeTab === 'services' 
                                        ? 'bg-blue-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-blue-50'
                                }`}
                            >
                                Services
                            </button>
                            <button
                                onClick={handleManageBeforeAfterImages}
                                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                                    activeTab === 'images'
                                        ? 'bg-green-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-green-50'
                                }`}
                            >
                                Before/After Images
                            </button>
                            <button
                                onClick={handleManageClinicImages}
                                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                                    activeTab === 'clinicImages'
                                        ? 'bg-orange-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-orange-50'
                                }`}
                            >
                                Clinic Images
                            </button>
                            <button
                                onClick={handleManageDoctors}
                                className={`px-3 sm:px-6 py-2 rounded-lg font-medium transition-colors text-sm sm:text-base ${
                                    activeTab === 'doctors'
                                        ? 'bg-purple-600 text-white'
                                        : 'bg-gray-100 text-gray-700 hover:bg-purple-50'
                                }`}
                            >
                                Doctors
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Main Content */}
            <div className="py-4 sm:py-8">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {renderContent()}
                </div>
            </div>
        </div>
    );
};

export default ClinicDetails; 